import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Search, MapPin, X } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const ColoniaSearch = ({ onSelect, onClose, placeholder = "Ciudad, colonia o CP..." }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const search = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/colonias/search?q=${encodeURIComponent(val)}`);
        setResults(res.data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (item) => {
    onSelect(item);
    setQuery("");
    setResults([]);
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid="colonia-search-wrapper">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-violet-500" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => search(e.target.value)}
          className="pl-9 pr-9 h-11 border-2 border-violet-300 focus:border-violet-500 bg-violet-50/50 text-sm"
          data-testid="colonia-search-input"
        />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            data-testid="colonia-search-close"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading && (
        <div className="absolute z-[100] left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-2xl p-3 text-center text-sm text-zinc-500">
          Buscando...
        </div>
      )}

      {!loading && results.length > 0 && (
        <div
          className="absolute z-[100] left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-2xl max-h-[45vh] overflow-y-auto"
          data-testid="colonia-search-results"
        >
          {results.map((item, idx) => (
            <button
              key={`${item.colonia}-${item.cp}-${idx}`}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full text-left px-3 py-2.5 hover:bg-violet-50 active:bg-violet-100 border-b border-zinc-100 last:border-0 transition-colors"
              data-testid={`colonia-result-${idx}`}
            >
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-sm text-zinc-800 leading-tight">{item.colonia}</span>
                    <span className="font-mono text-sm font-bold text-violet-600 shrink-0 bg-violet-50 px-1.5 py-0.5 rounded">{item.cp}</span>
                  </div>
                  <span className="text-xs text-zinc-500 leading-tight">{item.ciudad || item.municipio}, {item.estado}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-[100] left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-2xl p-3 text-center text-sm text-zinc-500">
          No se encontraron resultados
        </div>
      )}
    </div>
  );
};

export default ColoniaSearch;
